import { type Keyframes, keyframes } from '@emotion/react';
import { CssBaseline, GlobalStyles, tabClasses, type Theme } from '@mui/material';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';

const Book = new URL('./fonts/CircularXXWeb-Book.woff2', import.meta.url);
const BookBold = new URL('./fonts/CircularXXWeb-Bold.woff2', import.meta.url);
const BookItalic = new URL('./fonts/CircularXXWeb-Italic.woff2', import.meta.url);
const BookBoldItalic = new URL('./fonts/CircularXXWeb-BoldItalic.woff2', import.meta.url);

declare module '@mui/material' {
  interface ButtonPropsColorOverrides {
    white: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    primary: true;
    secondary: true;
    text: true;
  }
}

declare module '@mui/material/styles' {
  interface Theme {
    constants: Record<string, number>;
    animations: Record<string, Keyframes>;
  }
  // allow configuration using `createTheme`
  interface ThemeOptions {
    constants?: Record<string, number>;
    animations?: Record<string, Keyframes>;
  }

  interface Palette {
    primary: Palette['primary'];
    secondary: Palette['primary'];
    warning: Palette['primary'];
    error: Palette['primary'];
    success: Palette['primary'];
    neutral: Palette['primary'];
    border: Palette['primary'];
    monotone: Palette['primary'];
    chart: Palette['primary'];
    info: Palette['primary'];
  }

  interface PaletteOptions {
    primary?: PaletteOptions['primary'];
    secondary?: PaletteOptions['primary'];
    warning?: PaletteOptions['primary'];
    error?: PaletteOptions['primary'];
    success?: PaletteOptions['primary'];
    neutral?: PaletteOptions['primary'];
    border?: PaletteOptions['primary'];
    monotone?: PaletteOptions['primary'];
    chart?: PaletteOptions['primary'];
    info?: PaletteOptions['primary'];
  }
}

const theme = createTheme({
  animations: {
    wiggle: keyframes`
    0%, 100% {
      transform: rotate(0deg);
    }
    
    17%, 50%, 84% {
      transform: rotate(5deg);
    }
    
    35%, 67% {
      transform: rotate(-5deg);
    `,
  },
  components: {
    MuiAccordion: {
      styleOverrides: {
        root: {
          marginBottom: '16px',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          fontSize: '16px',
        },
      },
      variants: [
        {
          props: { severity: 'info' },
          style: ({ theme }) => ({
            '& .MuiAlert-icon': {
              color: theme.palette.primary.main,
            },
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.main,
          }),
        },
        {
          props: { severity: 'success' },
          style: ({ theme }) => ({
            '& .MuiAlert-icon': {
              color: theme.palette.success.main,
            },
            backgroundColor: theme.palette.success.light,
            color: theme.palette.success.main,
          }),
        },
      ],
    },
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          background: theme.palette.common.white,
          borderBottom: `2px solid ${theme.palette.divider}`,
          boxShadow: 'none',
          zIndex: theme.zIndex.drawer + 1,
        }),
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: () => ({
          minWidth: 'auto',
          textTransform: 'none',
          whiteSpace: 'nowrap',
        }),
      },
      variants: [
        {
          props: { variant: 'outlined' },
          style: ({ theme }) => ({
            '&:hover': {
              fontWeight: 'bold',
            },
            backgroundColor: theme.palette.common.white,
            border: `1px solid ${theme.palette.border.main}`,
          }),
        },
        {
          props: { variant: 'text' },
          style: ({ theme }) => ({
            '&.Mui-selected': {
              backgroundColor: '#F0F3F3',
              color: theme.palette.success.main,
            },
          }),
        },
      ],
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiChip: {
      variants: [
        {
          props: { color: 'success' },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.success.light,
            color: theme.palette.success.main,
          }),
        },
        {
          props: { color: 'primary' },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.main,
          }),
        },
      ],
    },
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: "CircularXXWeb-Book";
          src: url('${Book.href}') format("woff2");
          font-weight: 400;
          font-style: normal;
      }
      @font-face {
          font-family: "CircularXXWeb-Bold";
          src: url("${BookBold.href}") format("woff2");
          font-weight: 700;
          font-style: normal;
      }
      @font-face {
          font-family: "CircularXXWeb-Italic";
          src: url("${BookItalic.href}") format("woff2");
          font-weight: 400;
          font-style: italic;
      }
      @font-face {
          font-family: "CircularXXWeb-BoldItalic";
          src: url("${BookBoldItalic.href}") format("woff2");
          font-weight: 700;
          font-style: italic;
      }
      `,
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          paddingBottom: theme.spacing(3),
          paddingLeft: theme.spacing(3),
          paddingRight: theme.spacing(3),
          paddingTop: 0,
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ ownerState }) => {
          if (ownerState.anchor === 'bottom') {
            return {
              borderTopLeftRadius: '40px',
              borderTopRightRadius: '40px',
            };
          }
        },
      },
    },
    MuiGrid: {
      defaultProps: {
        spacing: 2,
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:hover': {
            backgroundColor: theme.palette.background.default,
          },
          backgroundColor: theme.palette.common.white,
        }),
      },
      variants: [
        {
          props: { color: 'primary' },
          style: ({ theme }) => ({
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
            backgroundColor: theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
            color: theme.palette.common.white,
          }),
        },
      ],
    },

    MuiImageListItemBar: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: () => ({
          textTransform: 'none',
          [`&.${tabClasses.selected}`]: {
            fontWeight: 'bold',
          },
        }),
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderBottom: `1px solid ${theme.palette.border.main}`,
        }),
      },
    },

    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
    },
  },
  constants: {
    footerHeight: 76.5,
    groundControlNavbarHeight: 63,
    sidebarWidth: 150,
  },
  palette: {
    background: {
      default: '#f9f9f9',
    },
    border: {
      main: '#E7E7E7',
    },
    chart: {
      light: '#d4bbff',
      main: '#6347FF',
    },
    error: {
      main: '#f63e3e',
    },
    info: {
      dark: '#014361',
      light: '#E5F6FD',
      main: '#0288D1',
    },
    monotone: {
      dark: '#F6F6F6',
      main: '#fff',
    },
    primary: {
      light: '#f6f5ff',
      main: '#6347FF',
    },
    secondary: {
      contrastText: '#fff',
      light: '#6E6D77',
      main: '#0D0C1D',
    },
    success: {
      dark: '#2FB554',
      light: '#EAF7ED',
      main: '#1B5E20',
    },
    warning: {
      main: '#ff891c',
    },
  },
  typography: {
    fontFamily: "'CircularXXWeb-Book', sans-serif",
  },
});

export type { Theme };
export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles styles={{ body: { WebkitFontSmoothing: 'antialiased', textRendering: 'optimizeLegibility' } }} />
      {children}
    </MuiThemeProvider>
  );
};
